import React, { memo } from "react";
import { Divider, HStack, Wrap, Text, VStack } from "@chakra-ui/react";
import {
  InfoIcon,
  ChatBubbleDotIcon,
  PinOutlineIcon,
  PaperclipIcon,
  ReminderIcon,
  LockIcon,
  CircleCheckIcon,
  CircleIcon,
  CheckedCircleIcon,
  TrashOutlineIcon,
  DoubleCircleArrowsIcon,
  AssignPersonIcon,
  PersonOutlineIcon,
  CalendarCheckIcon,
} from "modules/icons";
import colors from "app/colors";
import { SearchFilter, SearchFilterType, TaskStatusType } from "gql/graphql";
import FiltersDrawerToggleButton from "modules/messaging/search/filters-drawer-toggle-button";
import FiltersDrawerEmailSelector from "./filters-drawer-email-selector";
import FiltersDrawerTagSelector from "./filters-drawer-tag-selector";
import FiltersDrawerDateRangeSelector from "./filters-drawer-date-range-selector";
import FiltersDrawerTaskStatusToggleButton from "./filters-drawer-task-status-toggle-button";
import FiltersDrawerTaskPriorityToggleButton from "./filters-drawer-task-priority-toggle-button";
import FiltersDrawerThreadUserSelector from "./filters-drawer-thread-user-selector";

interface IProps {
  newSearchFilters: SearchFilter[];
  setNewSearchFilters: React.Dispatch<React.SetStateAction<SearchFilter[]>>;
}

const TaskFilters = ({ newSearchFilters, setNewSearchFilters }: IProps) => (
  <VStack w="full" spacing={4} divider={<Divider />}>
    <VStack w="full">
      <HStack w="full">
        <InfoIcon fill={colors.blackSecondary} boxSize={5} />
        <Text
          fontFamily="Outfit"
          color={colors.blackSecondary}
          fontSize={18}
          fontWeight={600}
        >
          Attributes
        </Text>
      </HStack>
      <Wrap w="full">
        <FiltersDrawerToggleButton
          title="Unread"
          icon={<ChatBubbleDotIcon fill={colors.gray50} boxSize={4} />}
          searchFilterType={SearchFilterType.IsNotSeenByMe}
          newSearchFilters={newSearchFilters}
          setNewSearchFilters={setNewSearchFilters}
        />
        <FiltersDrawerToggleButton
          title="Pinned"
          icon={<PinOutlineIcon fill={colors.gray50} boxSize={4} />}
          searchFilterType={SearchFilterType.IsPinnedByMe}
          newSearchFilters={newSearchFilters}
          setNewSearchFilters={setNewSearchFilters}
        />
        <FiltersDrawerToggleButton
          title="Attachments"
          icon={<PaperclipIcon fill={colors.gray50} boxSize={4} />}
          searchFilterType={SearchFilterType.HasAttachments}
          newSearchFilters={newSearchFilters}
          setNewSearchFilters={setNewSearchFilters}
        />
        <FiltersDrawerToggleButton
          title="Reminders"
          icon={<ReminderIcon fill={colors.gray50} boxSize={4} />}
          searchFilterType={SearchFilterType.IsReminder}
          newSearchFilters={newSearchFilters}
          setNewSearchFilters={setNewSearchFilters}
        />
        <FiltersDrawerToggleButton
          title="Private"
          icon={<LockIcon fill={colors.gray50} boxSize={4} />}
          searchFilterType={SearchFilterType.IsLocked}
          newSearchFilters={newSearchFilters}
          setNewSearchFilters={setNewSearchFilters}
        />
      </Wrap>
    </VStack>
    <VStack w="full">
      <HStack w="full">
        <CircleCheckIcon fill={colors.blackSecondary} boxSize={5} />
        <Text
          fontFamily="Outfit"
          color={colors.blackSecondary}
          fontSize={18}
          fontWeight={600}
        >
          Tasks
        </Text>
      </HStack>
      <Wrap w="full">
        <FiltersDrawerTaskPriorityToggleButton
          newSearchFilters={newSearchFilters}
          setNewSearchFilters={setNewSearchFilters}
        />
        <FiltersDrawerTaskStatusToggleButton
          statusType={TaskStatusType.NotStarted}
          icon={<CircleIcon boxSize={4} fill={colors.gray50} />}
          newSearchFilters={newSearchFilters}
          setNewSearchFilters={setNewSearchFilters}
        />
        <FiltersDrawerTaskStatusToggleButton
          statusType={TaskStatusType.OnHold}
          icon={<InfoIcon boxSize={4} fill={colors.gray50} />}
          newSearchFilters={newSearchFilters}
          setNewSearchFilters={setNewSearchFilters}
        />
        <FiltersDrawerTaskStatusToggleButton
          statusType={TaskStatusType.InProgress}
          icon={<DoubleCircleArrowsIcon boxSize={4} fill={colors.gray50} />}
          newSearchFilters={newSearchFilters}
          setNewSearchFilters={setNewSearchFilters}
        />
        <FiltersDrawerTaskStatusToggleButton
          statusType={TaskStatusType.Abandoned}
          icon={<TrashOutlineIcon boxSize={4} fill={colors.gray50} />}
          newSearchFilters={newSearchFilters}
          setNewSearchFilters={setNewSearchFilters}
        />
        <FiltersDrawerTaskStatusToggleButton
          statusType={TaskStatusType.Complete}
          icon={<CheckedCircleIcon boxSize={4} fill={colors.gray50} />}
          newSearchFilters={newSearchFilters}
          setNewSearchFilters={setNewSearchFilters}
        />
      </Wrap>
    </VStack>
    <FiltersDrawerThreadUserSelector
      newSearchFilters={newSearchFilters}
      setNewSearchFilters={setNewSearchFilters}
      title="Assigned To"
      type={SearchFilterType.TaskAssigneeUserIdList}
      icon={<AssignPersonIcon fill={colors.blackSecondary} boxSize={5} />}
    />
    <FiltersDrawerThreadUserSelector
      newSearchFilters={newSearchFilters}
      setNewSearchFilters={setNewSearchFilters}
      title="Task Creator"
      type={SearchFilterType.CreatedUserIdList}
      icon={<PersonOutlineIcon fill={colors.blackSecondary} boxSize={5} />}
    />
    <FiltersDrawerEmailSelector
      newSearchFilters={newSearchFilters}
      setNewSearchFilters={setNewSearchFilters}
    />
    <FiltersDrawerDateRangeSelector
      title="Task Due Date Range"
      startDateSearchFilterType={SearchFilterType.TaskDueStartDate}
      endDateSearchFilterType={SearchFilterType.TaskDueEndDate}
      dateRangeSearchFilterType={SearchFilterType.TaskDueDateRangeType}
      newSearchFilters={newSearchFilters}
      setNewSearchFilters={setNewSearchFilters}
      icon={<CalendarCheckIcon fill={colors.blackSecondary} boxSize={5} />}
    />
    <FiltersDrawerDateRangeSelector
      title="Task Complete Date Range"
      startDateSearchFilterType={SearchFilterType.TaskCompleteStartDate}
      endDateSearchFilterType={SearchFilterType.TaskCompleteEndDate}
      dateRangeSearchFilterType={SearchFilterType.TaskCompleteDateRangeType}
      newSearchFilters={newSearchFilters}
      setNewSearchFilters={setNewSearchFilters}
      icon={<CalendarCheckIcon fill={colors.blackSecondary} boxSize={5} />}
    />
    <FiltersDrawerDateRangeSelector
      title="Date Message Sent"
      startDateSearchFilterType={SearchFilterType.StartDate}
      endDateSearchFilterType={SearchFilterType.EndDate}
      dateRangeSearchFilterType={SearchFilterType.DateRangeType}
      newSearchFilters={newSearchFilters}
      setNewSearchFilters={setNewSearchFilters}
    />
    <FiltersDrawerTagSelector
      newSearchFilters={newSearchFilters}
      setNewSearchFilters={setNewSearchFilters}
    />
  </VStack>
);

export default memo(TaskFilters);
